import { FC, useEffect, useState } from 'react';
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { fetchCache } from 'src/api/caching/cacheUtils';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useDrugListAnalyticStore from 'src/hooks/useDrugListAnalyticStore';
import getDrugPage from 'src/api/requests/getDrugPage';
// import { ItemListName } from 'src/api/analytic.types';
// import { getAnalyticPreparedDrugList } from 'src/hooks/useAnalytics/useAnalytics.helpers';
import getDrug from '../../../api/requests/getDrug';
import {
  AllAnalogues,
  Category,
  DrugItem,
  DrugReview,
  PriceHistory
} from '../../../api/types';
import useLocationStore from '../../../hooks/useLocationStore';
import {
  getProductAnaloguePageMetaData,
  getProductPageMetaData,
  isNoIndexing
} from '../../../utils/seo';
import { generateDrugPageLink } from '../../../utils/links';
import getSearchForRenamedDrug from '../../../api/requests/getSearchForRenamedDrug';
import MetaProduct from '../../../page-components/drug/components/MetaProduct';
import getAnalogues from '../../../api/requests/getAnalogues';
import {
  GeneralServerProps,
  generalSSRProps,
  getCacheConfig,
  setSSRCacheControl
} from '../../../ssr/generalSsrUtil';
import DrugContainer from '../../../page-components/drug/DrugContainer';
import {
  CustomErrorProps,
  triggerCustomStatusCodeRedirect,
  useHandleCustomErrorRedirect
} from '../../../ssr/errorStatusCodeRedirect';
import { TabTypes } from '../../../page-components/drug/utils';

interface Props extends GeneralServerProps, CustomErrorProps {
  ssrDrugData: DrugItem;
  categoriesList: Category[];
  analoguesList: AllAnalogues | null;
  // otherPopularDrugsList: DrugItem[];
  priceHistory: PriceHistory[];
  reviewsList: DrugReview[];
}

const DrugPage: FC<Props> = ({
  ssrDrugData,
  analoguesList,
  // otherPopularDrugsList,
  priceHistory,
  hasError,
  statusCode,
  reviewsList
}) => {
  const { redirectToStatusCodePage } = useHandleCustomErrorRedirect({
    hasError,
    statusCode
  });
  const [drugData, setDrugData] = useState<DrugItem | null>(null);
  const { query, locale } = useRouter();
  const { userLocation, userLocationUpdatedManually } = useLocationStore();
  const { sendViewItemAnalytic } = useAnalytics();
  const { drugListAnalytics: drugListAnalytic } = useDrugListAnalyticStore();

  const translitName = query?.translitName as string;

  const currentDrugData = {
    ...(drugData ?? ssrDrugData),
    analytics: drugListAnalytic
  };

  // We need 2 separate useEffects for location change case and translitName change
  // in order to handle correct page data update and avoid unneccesary re-renders on programmatic client-side userLocation changes
  useEffect(() => {
    // Reload data on the client side if location was updated manually
    if (userLocation?.name && userLocationUpdatedManually) {
      getDrug({ id: translitName })
        .then((data) => {
          setDrugData(data);
        })
        .catch(() => {
          setDrugData(null);
        });
    }
  }, [userLocation?.name, userLocationUpdatedManually]);

  useEffect(() => {
    // Reload data on the client side only if translitName changed
    if (translitName) {
      getDrug({ id: translitName })
        .then((data) => {
          setDrugData(data);
        })
        .catch(() => {
          setDrugData(null);
        });
    }
  }, [translitName]);

  useEffect(() => {
    if (currentDrugData?.translitName) {
      setTimeout(() => {
        sendViewItemAnalytic(currentDrugData);
      });
    }
  }, [currentDrugData?.translitName, userLocation?.name]);

  if (hasError) {
    return redirectToStatusCodePage();
  }

  const getSeoData =
    query?.tab?.[0] === TabTypes.ANALOGUE
      ? getProductAnaloguePageMetaData
      : getProductPageMetaData;

  const seoData = getSeoData(currentDrugData, locale);
  const metaUrl = generateDrugPageLink({ translitName, locale }).pathname;

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description}></meta>
        <meta
          name="robots"
          content={isNoIndexing ? 'noindex' : currentDrugData?.seo.robots}
          key="robots"
        />
        <MetaProduct
          description={seoData.description}
          ssrDrugData={ssrDrugData}
          reviewsList={reviewsList}
          metaUrl={metaUrl}
        />
      </Head>
      <DrugContainer
        drugData={currentDrugData}
        analoguesList={analoguesList}
        // otherPopularDrugsList={otherPopularDrugsList}
        priceHistory={priceHistory}
        reviewsList={reviewsList}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchCache<GetServerSidePropsResult<{ [key: string]: any }>>(
    getCacheConfig(context),
    async () => {
      const {
        res,
        req: { cookies },
        query
      } = context;
      const translitName = query?.translitName as string;
      const generalProps = await generalSSRProps(context);

      const isAnalogPage = query?.tab?.[0] === TabTypes.ANALOGUE;
      // REVIEWS tab provided. Code 301 should be returned since these pages shoudln't be available for crawler bots
      // review logic is handled with hashes #reviews and #reviewform
      const isReviewsPage = query?.tab?.[0] === TabTypes.REVIEWS;
      // ANALOGUE is the only specific tab allowed for now. Any other URL are defined as a bad one and throws 301
      const isIncorrectTabProvided =
        query?.tab?.[0] && query?.tab?.[0] !== TabTypes.ANALOGUE;

      if (isReviewsPage) {
        return triggerCustomStatusCodeRedirect(
          res,
          301,
          generateDrugPageLink({ translitName, locale: context?.locale })
            .pathname
        );
      }

      if (isIncorrectTabProvided) {
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      const drugPageDataRequest = getDrugPage(
        { translitName },
        {
          cookies,
          ip: generalProps.ip,
          locale: context?.locale,
          useCache: true
        }
      );

      const [drugPageData] = await Promise.all([drugPageDataRequest]);

      const {
        drug: ssrDrugData,
        // popularDrugs: otherPopularDrugsList,
        drugPriceHistory: priceHistory,
        drugRates: reviewsList
      } = drugPageData ?? {
        drug: null,
        popularDrugs: null,
        drugPriceHistory: null,
        drugRates: null
      };

      let analoguesList = null;

      if (isAnalogPage && !ssrDrugData?.withAnalogs) {
        // Redirect to 410 if drug doesn't have analogs for /analogi page
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      // TODO: cover analog page with e2e test
      if (isAnalogPage) {
        // Call analogs only for /analogi tab
        const result = await getAnalogues(
          { id: ssrDrugData?.id },
          {
            cookies,
            ip: generalProps?.ip,
            locale: context?.locale,
            useCache: true
          }
        );
        // TODO: cover analogs error with e2e test
        if (
          !result ||
          (result.fullAnalogue?.length === 0 &&
            result.otherFormAnalogue?.length === 0 &&
            result.sameEffectFormAnalogue?.length === 0)
        ) {
          // Redirect to 410 if drug analogues data not found for /analogi page
          return triggerCustomStatusCodeRedirect(res, 410);
        }
        analoguesList = result;
      }

      if (!ssrDrugData) {
        // No drug data found. The product might have been renamed. Check for renamed product data
        const renamedDrugData = await getSearchForRenamedDrug({ translitName });
        if (renamedDrugData?.translitName) {
          // TODO: cover this case with e2e tests!
          // Renamed drug found. Permanently redirect to that product
          return {
            redirect: {
              permanent: true,
              destination: generateDrugPageLink({
                translitName: renamedDrugData.translitName,
                locale: context?.locale
              }).pathname
            }
          };
        }

        // Redirect to 410 if product and renamed version not found
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      return {
        props: {
          ...generalProps,
          ssrDrugData,
          analoguesList,
          // otherPopularDrugsList: getAnalyticPreparedDrugList({
          //   drugs: otherPopularDrugsList,
          //   itemListName: ItemListName.Default
          // }),
          priceHistory,
          reviewsList
        }
      };
    }
  );
};

export default DrugPage;
